/**
 * Image compression utility for photo documentation
 * Natywna implementacja oparta na Canvas + URL.createObjectURL – bezpieczna dla pamięci (bez Base64).
 * Eliminuje OOM na iOS i blokowanie UI.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxWidthOrHeight?: number;
  initialQuality?: number;
  timeoutMs?: number;
}

/**
 * Kompresuje obraz używając natywnego Canvas i URL.createObjectURL.
 * Nie ładuje całego pliku do Base64 – minimalne zużycie RAM.
 */
export async function compressImage(
  file: File,
  optionsOrMaxWidth: CompressionOptions | number = {},
  quality?: number
): Promise<File> {
  const opts = typeof optionsOrMaxWidth === 'number'
    ? { maxWidthOrHeight: optionsOrMaxWidth, initialQuality: quality ?? 0.6 }
    : optionsOrMaxWidth;

  const maxWidthOrHeight = opts.maxWidthOrHeight ?? opts.maxWidth ?? 1024;
  const qualityVal = opts.initialQuality ?? 0.6;
  const timeoutMs = opts.timeoutMs ?? 8000;

  return new Promise((resolve) => {
    const img = new Image();
    let objectUrl: string | null = null;
    let settled = false;
    let watchdog: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (watchdog) {
        clearTimeout(watchdog);
        watchdog = null;
      }
      img.onload = null;
      img.onerror = null;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    const settleOnce = (result: File) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    watchdog = setTimeout(() => {
      console.warn(`[Kompresja] Timeout ${timeoutMs}ms przekroczony. Zwracam oryginał.`);
      settleOnce(file);
    }, timeoutMs);

    img.onload = () => {
      try {
        let width = img.width;
        let height = img.height;

        const maxDim = Math.max(width, height);
        if (maxDim > maxWidthOrHeight) {
          const scale = maxWidthOrHeight / maxDim;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('[Kompresja] Brak obsługi Canvas. Zwracam oryginał.');
          settleOnce(file);
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error('[Kompresja] toBlob zwrócił null. Zwracam oryginał.');
              settleOnce(file);
              return;
            }

            try {
              const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              settleOnce(compressedFile);
            } catch (error) {
              console.error('[Kompresja] Błąd tworzenia pliku po kompresji. Zwracam oryginał.', error);
              settleOnce(file);
            }
          },
          'image/jpeg',
          qualityVal
        );
      } catch (error) {
        console.error('[Kompresja] Błąd podczas kompresji. Zwracam oryginał.', error);
        settleOnce(file);
      }
    };

    img.onerror = () => {
      console.error('[Kompresja] Nie udało się załadować obrazu. Zwracam oryginał.');
      settleOnce(file);
    };

    try {
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (error) {
      console.error('[Kompresja] Nie udało się utworzyć object URL. Zwracam oryginał.', error);
      settleOnce(file);
    }
  });
}

/**
 * Backward-compatible alias used by mobile capture flows.
 * Keeps naming explicit that implementation is Canvas/ObjectURL based.
 */
export async function compressImageSafeCanvas(
  file: File,
  optionsOrMaxWidth: CompressionOptions | number = {},
  quality?: number
): Promise<File> {
  return compressImage(file, optionsOrMaxWidth, quality);
}
